'use client';

import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Info } from 'lucide-react';
import { getImageUrl, IMAGE_SIZES, TMDBMovie } from '../lib/tmdb';
import { Skeleton } from './ui/skeleton';
import { useIsTVDevice, useTVRow } from '../hooks/useTVNavigation';

interface MovieRowProps {
  title: string;
  movies: TMDBMovie[];
  onMovieClick: (movie: TMDBMovie) => void;
  onMovieInfo?: (movie: TMDBMovie) => void;
  loading?: boolean;
  rowIndex?: number;
  previousRowId?: string;
  nextRowId?: string;
  upTarget?: string;
}

export default function MovieRow({ 
  title, 
  movies, 
  onMovieClick, 
  onMovieInfo,
  loading = false,
  rowIndex = 0,
  previousRowId,
  nextRowId,
  upTarget = 'hero-play-button'
}: MovieRowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const isTV = useIsTVDevice();
  const movieRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Generate row ID from title
  const rowId = title.toLowerCase().replace(/\s+/g, '-');
  
  // Register row with navigation system
  const { registerCard, unregisterCard } = useTVRow(
    rowId,
    rowIndex,
    movies.length,
    previousRowId,
    nextRowId,
    upTarget
  );

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: isTV ? 'auto' : 'smooth', // Instant scroll on TV for better performance
      });
    }
  };

  // Register cards with navigation system when refs are available
  useEffect(() => {
    if (!isTV || movies.length === 0) return;
    
    // Register cards that have refs
    movies.forEach((_, index) => {
      const ref = movieRefs.current[index];
      if (ref) {
        const cardRef = { current: ref } as React.RefObject<HTMLElement>;
        registerCard(index, cardRef);
      }
    });
    
    return () => {
      movies.forEach((_, index) => {
        unregisterCard(index);
      });
    };
  }, [isTV, movies.length, registerCard, unregisterCard]);
  
  // Listen for focus changes on all cards to update local state
  useEffect(() => {
    if (!isTV || movies.length === 0) return;
    
    const handleFocus = (index: number) => () => {
      setFocusedIndex(index);
    };
    
    const handleBlur = () => {
      // Clear focused index when any card in this row loses focus
      setFocusedIndex(-1);
    };
    
    const focusHandlers: (() => void)[] = [];
    const blurHandlers: (() => void)[] = [];
    
    movies.forEach((_, index) => {
      const ref = movieRefs.current[index];
      if (ref) {
        const focusHandler = handleFocus(index);
        const blurHandler = handleBlur;
        ref.addEventListener('focus', focusHandler);
        ref.addEventListener('blur', blurHandler);
        focusHandlers.push(focusHandler);
        blurHandlers.push(blurHandler);
      }
    });
    
    return () => {
      movies.forEach((_, index) => {
        const ref = movieRefs.current[index];
        if (ref) {
          if (focusHandlers[index]) {
            ref.removeEventListener('focus', focusHandlers[index]);
          }
          if (blurHandlers[index]) {
            ref.removeEventListener('blur', blurHandlers[index]);
          }
        }
      });
    };
  }, [isTV, movies.length]);
  
  // Handle Enter/Space to select movie
  useEffect(() => {
    if (!isTV) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (focusedIndex === -1) return;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < movies.length) {
          onMovieClick(movies[focusedIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTV, focusedIndex, movies, onMovieClick]);

  if (loading) {
    return (
      <div className="mb-8 lg:mb-12">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="flex gap-2 overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32 lg:h-48 w-[calc(50%-0.25rem)] sm:w-[calc(33.333%-0.33rem)] md:w-[calc(25%-0.375rem)] lg:w-[calc(16.666%-0.42rem)] flex-shrink-0 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!movies || movies.length === 0) {
    return null;
  }

  return (
    <div className="mb-10 lg:mb-16 relative movie-row-container">
      {/* Row title */}
      <h2 className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6 text-white">
        {title}
      </h2>

      {/* Scroll buttons */}
      <button
        onClick={() => scroll('left')}
        className="absolute left-0 top-[45%] -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 p-2 lg:p-3 rounded-r-lg opacity-0 hover:opacity-100 transition-all duration-300 hidden sm:block backdrop-blur-sm"
        aria-label="Scroll left"
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      >
        <ChevronLeft className="w-6 h-6 lg:w-8 lg:h-8" />
      </button>

      <button
        onClick={() => scroll('right')}
        className="absolute right-0 top-[45%] -translate-y-1/2 z-10 bg-black/70 hover:bg-black/90 p-2 lg:p-3 rounded-l-lg opacity-0 hover:opacity-100 transition-all duration-300 hidden sm:block backdrop-blur-sm"
        aria-label="Scroll right"
        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      >
        <ChevronRight className="w-6 h-6 lg:w-8 lg:h-8" />
      </button>

      {/* Movies container */}
      <div
        ref={scrollContainerRef}
        className={`scroll-container ${isTV ? '' : 'gap-4 lg:gap-5 pb-2'}`}
      >
        {movies.map((movie, index) => (
          <div
            key={movie.id}
            className={`flex-shrink-0 ${isTV ? 'w-[180px] lg:w-[180px]' : 'w-[145px] sm:w-[165px] md:w-[185px] lg:w-[210px]'}`}
          >
            <MovieCard 
              movie={movie} 
              onClick={() => onMovieClick(movie)}
              onInfo={onMovieInfo ? () => onMovieInfo(movie) : undefined}
              index={index}
              isFocused={isTV && focusedIndex === index}
              isTV={isTV}
              onFocus={() => setFocusedIndex(index)}
              ref={(el) => {
                movieRefs.current[index] = el;
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface MovieCardProps {
  movie: TMDBMovie;
  onClick: () => void;
  onInfo?: () => void;
  index: number;
  isFocused?: boolean;
  isTV?: boolean;
  onFocus?: () => void;
}

const MovieCard = React.memo(React.forwardRef<HTMLDivElement, MovieCardProps>(({ movie, onClick, onInfo, index, isFocused, isTV, onFocus }, ref) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const posterUrl = getImageUrl(movie.poster_path, IMAGE_SIZES.poster.medium);
  const rating = movie.vote_average ? movie.vote_average.toFixed(1) : 'N/A';
  const year = movie.release_date ? new Date(movie.release_date).getFullYear() : null;

  return (
    <motion.div
      ref={ref}
      whileHover={isTV ? {} : { y: -8 }} // Disable hover animation on TV
      animate={isFocused ? { scale: 1.05 } : { scale: 1 }}
      transition={{ duration: isTV ? 0.1 : 0.3, ease: 'easeOut' }} // Faster on TV
      className={`movie-card cursor-pointer group ${isFocused ? 'tv-focused' : ''} ${isTV ? 'tv-card' : ''}`}
      role="button"
      tabIndex={isTV ? 0 : -1}
      aria-label={`View details for ${movie.title}`}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Poster */}
      <div 
        className="relative aspect-[2/3] bg-gray-800 rounded-lg overflow-hidden shadow-lg group-hover:shadow-2xl transition-shadow duration-300"
        onClick={onClick}
      >
        <img
          src={imageError ? '/placeholder-movie.png' : posterUrl}
          alt={movie.title}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => {
            if (!imageError) {
              if (process.env.NODE_ENV === 'development') {
              console.error('Failed to load poster for:', movie.title);
              }
              setImageError(true);
            }
          }}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
          <div className="bg-white rounded-full p-3 lg:p-4 shadow-xl transform scale-90 group-hover:scale-100 transition-transform duration-200">
            <Play className="w-5 h-5 lg:w-7 lg:h-7 text-black fill-current" />
          </div>
        </div>

        {/* Info button (top-left on hover) */}
        {onInfo && (
          <button
            onClick={(e) => { e.stopPropagation(); onInfo(); }}
            className="absolute top-2 left-2 p-1.5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/90 z-10"
            aria-label={`More info about ${movie.title}`}
          >
            <Info className="w-4 h-4" />
          </button>
        )}

        {/* Badge for high rated movies */}
        {movie.vote_average >= 8 && (
          <div className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold px-2.5 py-1 rounded shadow-lg">
            TOP
          </div>
        )}
      </div>

      {/* Title and info - Always visible */}
      <div 
        className="mt-3 space-y-2 px-1"
        onClick={onClick}
      >
        <h3 className="font-semibold text-sm lg:text-base text-white line-clamp-2 leading-tight group-hover:text-red-400 transition-colors duration-200">
          {movie.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <svg
              className="w-3.5 h-3.5 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="font-medium text-gray-300">{rating}</span>
          </span>
          {year && (
            <span className="text-gray-400">
              {year}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}), (prevProps, nextProps) => {
  // Custom comparison for better performance - only re-render if these change
  return (
    prevProps.movie.id === nextProps.movie.id &&
    prevProps.isFocused === nextProps.isFocused &&
    prevProps.isTV === nextProps.isTV
  );
});

MovieCard.displayName = 'MovieCard';

